import {
  BuildingProperties,
  ComplexProperties,
  LevelProperties,
  RealEstateProperties,
  RoomProperties
} from '../types'
import { GeoFilters, useCurrentFeatureFilters } from '@/composables/useFilters'
import { computed, watch } from 'vue'
import isEqual from 'lodash/isEqual'
import { useStore } from 'vuex'

type TransferrableState = {
  complex?: ComplexProperties
  realEstate?: RealEstateProperties
  buildings: BuildingProperties[]
  level?: LevelProperties
  room?: RoomProperties
}

function getObjectWithId<T extends { id: any }>(obj: unknown): T | undefined {
  // Check if obj is an object and has own property 'id'
  if (typeof obj === 'object' && obj !== null && Object.hasOwn(obj, 'id')) {
    return obj as T // Type assertion with generic type T
  }
  return undefined
}

function getObjectWithBelongsToId<T extends { belongsToId: any }>(obj: unknown): T | undefined {
  // Check if obj is an object and has own property 'id'
  if (typeof obj === 'object' && obj !== null && Object.hasOwn(obj, 'belongsToId')) {
    return obj as T // Type assertion with generic type T
  }
  return undefined
}

/**
 * @description
 * This hook is used to transfer state from vuex store to analytics filters and vice versa.
 */
export function useTransferState(store: any) {
  const _store = useStore()
  const state = computed<TransferrableState>(() => ({
    complex: getObjectWithBelongsToId(_store.state.mapview?.selectedComplex),
    realEstate: getObjectWithBelongsToId(_store.state.mapview?.selectedRealEstate),
    buildings: getObjectWithId(_store.state.mapview?.selectedBuilding)
      ? [store.state.mapview?.selectedBuilding]
      : [],
    level: getObjectWithId(_store.state.selectedLevel),
    room: getObjectWithId(_store.state.selectedRoom)
  }))

  const _filters = useCurrentFeatureFilters()

  const filterComplex = computed<ComplexProperties | undefined>(() => {
    const value = _filters.value?.submittedGeographicalFilters?.complex
    return getObjectWithBelongsToId(value)
  })

  const filterRealEstate = computed<RealEstateProperties | undefined>(() => {
    const value = _filters.value?.submittedGeographicalFilters?.realEstate
    return getObjectWithBelongsToId(value)
  })

  const filterBuildings = computed<BuildingProperties[]>(() => {
    const value = _filters.value?.submittedGeographicalFilters?.buildings ?? []
    return value?.map((x) => getObjectWithId(x)) as BuildingProperties[]
  })

  const filterLevel = computed<LevelProperties | undefined>(() => {
    const value = _filters.value?.submittedGeographicalFilters?.level
    return getObjectWithId(value)
  })

  const filterRoom = computed<RoomProperties | undefined>(() => {
    const value = _filters.value?.submittedGeographicalFilters?.room
    return getObjectWithId(value)
  })

  const filters = computed<TransferrableState | undefined>(() => {
    if (!_filters.value) {
      return undefined
    }
    return {
      complex: filterComplex.value,
      realEstate: filterRealEstate.value,
      buildings: filterBuildings.value,
      level: filterLevel.value,
      room: filterRoom.value
    }
  })

  function toGeoFilters(state: TransferrableState): GeoFilters {
    return {
      complex: state.complex,
      realEstate: state.realEstate,
      buildings: state.buildings,
      level: state.buildings.length > 0 ? state.level : undefined,
      room: state.room
    }
  }

  watch(
    state,
    (newValue) => {
      if (!filters.value || isEqual(newValue, toGeoFilters(filters.value))) {
        return
      }
      _filters.value?.importGeographicalFilters(toGeoFilters(state.value))
    },
    { immediate: true }
  )

  watch(filters, async (newValue) => {
    if (isEqual(newValue, state.value) || !newValue) {
      return
    }
    await _store.dispatch('IMPORT_STATE', { ...newValue })
  })

  return {
    state,
    filters
  }
}
